/* eslint-disable @typescript-eslint/no-explicit-any */
import '@/styles/globals.css';
import App, { AppContext, AppProps } from 'next/app';
import { ChakraProvider } from '@chakra-ui/react';
import Head from 'next/head';
import { Amplify } from 'aws-amplify';
import { NextPage } from 'next';
import { useEffect, useRef } from 'react';
import customTheme from '@/styles/theme';
import DefaultSeo from '@/components/app/DefaultSeo';

import { awsConfig } from '@/utils/awsConfig';
import AuthGuard from '@/components/app/AuthGuard';
import { ToastContainer } from '@/utils/toast';

import getAppVersion from '@/utils/getAppVersion';
import { EmotionCacheProvider } from '@/components/EmotionCacheProvider';
import PageLoader from '@/components/app/PageLoader';
import { useStores } from '@/hooks/useStores';

Amplify.configure(awsConfig);

export type NextApplicationPage<P = unknown, IP = P> = NextPage<P, IP> & {
  requireAuth?: boolean;
};

interface AppPropsWithComponent extends AppProps {
  Component: NextApplicationPage;
  nonce: string;
}

export default function CustomApp({
  Component,
  pageProps,
  nonce,
}: AppPropsWithComponent) {
  const nonceRef = useRef(nonce);
  const {
    authStore: { autoLogin },
  } = useStores();

  useEffect(() => {
    if (nonce) {
      nonceRef.current = nonce;
    }
  }, [nonce]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      autoLogin();
    }
  }, [autoLogin]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, viewport-fit=cover"
        />
        <meta name="version" content={getAppVersion()} />
      </Head>
      <DefaultSeo />
      <EmotionCacheProvider nonce={nonceRef.current}>
        <ChakraProvider theme={customTheme}>
          {Component.requireAuth ? (
            <AuthGuard>
              <Component {...pageProps} />
            </AuthGuard>
          ) : (
            <PageLoader>
              <Component {...pageProps} />
            </PageLoader>
          )}
          <ToastContainer />
        </ChakraProvider>
      </EmotionCacheProvider>
    </>
  );
}

CustomApp.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context);

  const nonce = context.ctx.req?.headers['x-nonce'] as string;

  return { ...ctx, nonce };
};
